import React from 'react';
import { Label, Select, TextInput, Datepicker, Textarea, Checkbox } from 'flowbite-react';
import * as sched from 'sched';
import SelectWorkspaceUserId from '../common/SelectWorkspaceUserId';

// TODO restructure form fields
export interface WorkflowStepConditionFormData {
  type: sched.WorkflowStepCondition["type"];
  // Manual fields
  actorType?: sched.WorkflowAuthorizationSubject["type"];
  actorId?: string;
  // Work estimate (Manual tasks)
  workEstimateMin?: number;
  workEstimateExpected?: number;
  workEstimateMax?: number;
  // TimeElapsed fields
  durationSeconds?: number;
  // TimeReached fields
  targetTime?: string; // RFC3339 timestamp
  // WorkflowStateQuery fields
  stateQuery?: string;
  // Branch fields
  branchOptions?: Record<string, string>;
  multiSelect?: boolean;
  deciderType?: sched.WorkflowAuthorizationSubject["type"];
  deciderId?: string;
}

interface WorkflowStepConditionFormProps {
  value: WorkflowStepConditionFormData;
  onChange: (value: WorkflowStepConditionFormData) => void;
  workspaceId: string;
  errors?: Partial<Record<keyof WorkflowStepConditionFormData, string>>;
  touched?: Partial<Record<keyof WorkflowStepConditionFormData, boolean>>;
  /** Existing parameter keys used in the template, for the Parameter dropdown suggestions. */
  existingParameters?: string[];
}

const ParameterComboBox: React.FC<{
  value: string;
  onChange: (value: string) => void;
  existingParameters: string[];
}> = ({ value, onChange, existingParameters }) => {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as HTMLElement)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = existingParameters.filter(
    (p) => p.toLowerCase().includes(value.toLowerCase())
  );

  return (
    <div ref={containerRef} className="relative">
      <Label htmlFor="param-key">Parameter Name</Label>
      <div className="relative">
        <TextInput
          id="param-key"
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="e.g. developer, designer, reviewer"
        />
        {existingParameters.length > 0 && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-cm-text-muted hover:text-cm-text-secondary"
            onClick={() => setOpen(!open)}
            tabIndex={-1}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={open ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
            </svg>
          </button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <ul className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-cm-border-subtle bg-cm-bg-surface shadow-lg">
          {filtered.map((p) => (
            <li key={p}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-cm-bg-hover"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(p);
                  setOpen(false);
                }}
              >
                {p}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const WorkflowStepConditionForm: React.FC<WorkflowStepConditionFormProps> = ({
  value,
  onChange,
  workspaceId,
  errors = {},
  touched = {},
  existingParameters = [],
}) => {
  const handleTypeChange = (newType: WorkflowStepConditionFormData['type']) => {
    // Reset all fields when type changes
    onChange({
      type: newType,
      actorType: newType === 'Manual' ? 'Everyone' : undefined,
      actorId: undefined,
      durationSeconds: newType === 'TimeElapsed' ? 86400 : undefined, // Default 1 day
      targetTime: undefined,
      stateQuery: undefined,
      branchOptions: newType === 'Branch' ? {} : undefined,
      multiSelect: newType === 'Branch' ? false : undefined,
      deciderType: newType === 'Branch' ? 'Users' : undefined,
      deciderId: undefined,
    });
  };

  const handleFieldChange = (field: keyof WorkflowStepConditionFormData, fieldValue: any) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  const formatDateForInput = (isoString: string): string => {
    return new Date(isoString).toISOString().slice(0, 16);
  };

  const formatInputForDate = (inputValue: string): string => {
    return new Date(inputValue).toISOString();
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="completion-type">Completion Type</Label>
        <Select
          id="completion-type"
          value={value.type}
          onChange={(e) => handleTypeChange(e.target.value as WorkflowStepConditionFormData['type'])}
        >
          <option value="Immediate">Immediate</option>
          <option value="Manual">Task (manual work)</option>
          <option value="TimeElapsed">Time Elapsed</option>
          <option value="TimeReached">Time Reached</option>
          <option value="Branch">Branch Decision</option>
        </Select>
        {touched.type && errors.type && (
          <p className="mt-1 text-sm text-red-600">{errors.type}</p>
        )}
      </div>

      {/* Task (Manual) Fields */}
      {value.type === 'Manual' && (
        <div className="space-y-3 p-3 border border-cm-border-subtle rounded-md transition-all duration-300 ease-in-out animate-in slide-in-from-top-2">
          <h4 className="font-medium text-cm-text-primary">Task Settings</h4>

          <div>
            <Label htmlFor="actor-type">Assigned to</Label>
            <Select
              id="actor-type"
              value={value.actorType || 'Everyone'}
              onChange={(e) => {
                const newType = e.target.value;
                onChange({
                  ...value,
                  actorType: newType as any,
                  actorId: (newType === 'Users' || newType === 'Parameter') ? value.actorId : undefined,
                });
              }}
            >
              <option value="Everyone">Anyone</option>
              <option value="Users">Specific User</option>
              <option value="Parameter">Worker Parameter</option>
            </Select>
            <p className="mt-1 text-xs text-cm-text-muted">
              {value.actorType === 'Parameter'
                ? 'Assigned at launch time via a named parameter (e.g. "developer", "designer")'
                : value.actorType === 'Everyone'
                ? 'Any available worker can complete this task'
                : 'A specific user must complete this task'}
            </p>
            {touched.actorType && errors.actorType && (
              <p className="mt-1 text-sm text-red-600">{errors.actorType}</p>
            )}
          </div>

          {value.actorType === 'Users' && (
            <SelectWorkspaceUserId
              value={value.actorId || ''}
              onChange={(selectedValue) => handleFieldChange('actorId', selectedValue as string)}
              label="Select User"
              placeholder="Search for a user..."
              error={touched.actorId ? errors.actorId : undefined}
            />
          )}

          {value.actorType === 'Parameter' && (
            <ParameterComboBox
              value={value.actorId || ''}
              onChange={(v) => handleFieldChange('actorId', v)}
              existingParameters={existingParameters}
            />
          )}

          <div>
            <Label>Work Estimate (units)</Label>
            <p className="mb-2 text-xs text-cm-text-muted">
              Three-point estimate: optimistic, most likely, and pessimistic. Used for scheduling.
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label htmlFor="work-est-min" className="text-xs">Min</Label>
                <TextInput
                  id="work-est-min"
                  type="number"
                  min="0"
                  value={value.workEstimateMin ?? ''}
                  onChange={(e) => handleFieldChange('workEstimateMin', e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="Min"
                />
              </div>
              <div>
                <Label htmlFor="work-est-expected" className="text-xs">Expected</Label>
                <TextInput
                  id="work-est-expected"
                  type="number"
                  min="0"
                  value={value.workEstimateExpected ?? ''}
                  onChange={(e) => handleFieldChange('workEstimateExpected', e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="Expected"
                />
              </div>
              <div>
                <Label htmlFor="work-est-max" className="text-xs">Max</Label>
                <TextInput
                  id="work-est-max"
                  type="number"
                  min="0"
                  value={value.workEstimateMax ?? ''}
                  onChange={(e) => handleFieldChange('workEstimateMax', e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="Max"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Time Elapsed Fields */}
      {value.type === 'TimeElapsed' && (
        <div className="space-y-3 p-3 border border-cm-border-subtle rounded-md transition-all duration-300 ease-in-out animate-in slide-in-from-top-2">
          <h4 className="font-medium text-cm-text-primary">Time Elapsed Settings</h4>

          <div>
            <Label htmlFor="duration-days">Duration (days)</Label>
            <TextInput
              id="duration-days"
              type="number"
              value={value.durationSeconds != null ? String(value.durationSeconds / 86400) : ''}
              onChange={(e) => {
                const days = parseFloat(e.target.value) || 0;
                handleFieldChange('durationSeconds', Math.round(days * 86400));
              }}
              placeholder="Enter duration in days"
              min="0.01"
              step="0.5"
            />
            <div className="mt-1.5 flex items-center gap-1.5">
              {[
                { label: '1 day', days: 1 },
                { label: '7 days', days: 7 },
                { label: '30 days', days: 30 },
                { label: '90 days', days: 90 },
              ].map(({ label, days }) => (
                <button
                  key={days}
                  type="button"
                  onClick={() => handleFieldChange('durationSeconds', days * 86400)}
                  className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                    value.durationSeconds === days * 86400
                      ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700'
                      : 'bg-cm-bg-elevated border-cm-border-subtle text-cm-text-secondary hover:bg-cm-bg-hover'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {touched.durationSeconds && errors.durationSeconds && (
              <p className="mt-1 text-sm text-red-600">{errors.durationSeconds}</p>
            )}
          </div>
        </div>
      )}

      {/* Time Reached Fields */}
      {value.type === 'TimeReached' && (
        <div className="space-y-3 p-3 border border-cm-border-subtle rounded-md transition-all duration-300 ease-in-out animate-in slide-in-from-top-2">
          <h4 className="font-medium text-cm-text-primary">Time Reached Settings</h4>

          <div>
            <Label htmlFor="target-time">Target Date & Time</Label>
            <TextInput
              id="target-time"
              type="datetime-local"
              value={value.targetTime ? formatDateForInput(value.targetTime) : ''}
              onChange={(e) => handleFieldChange('targetTime', e.target.value ? formatInputForDate(e.target.value) : undefined)}
            />
            {touched.targetTime && errors.targetTime && (
              <p className="mt-1 text-sm text-red-600">{errors.targetTime}</p>
            )}
          </div>
        </div>
      )}

      {/* Workflow State Query Fields */}
      {value.type === 'WorkflowStateQuery' && (
        <div className="space-y-3 p-3 border border-cm-border-subtle rounded-md transition-all duration-300 ease-in-out animate-in slide-in-from-top-2">
          <h4 className="font-medium text-cm-text-primary">Workflow State Query Settings</h4>

          <div>
            <Label htmlFor="state-query">State Query</Label>
            <Textarea
              id="state-query"
              value={value.stateQuery || ''}
              onChange={(e) => handleFieldChange('stateQuery', e.target.value)}
              placeholder="Enter workflow state query"
              rows={3}
            />
            <p className="mt-1 text-sm text-cm-text-muted">
              Query to evaluate against the workflow state
            </p>
            {touched.stateQuery && errors.stateQuery && (
              <p className="mt-1 text-sm text-red-600">{errors.stateQuery}</p>
            )}
          </div>
        </div>
      )}

      {/* Branch Decision Fields */}
      {value.type === 'Branch' && (
        <div className="space-y-3 p-3 border border-cm-border-subtle rounded-md transition-all duration-300 ease-in-out animate-in slide-in-from-top-2">
          <h4 className="font-medium text-cm-text-primary">Branch Decision Settings</h4>

          <div>
            <Label>
              <Checkbox
                checked={value.multiSelect || false}
                onChange={(e) => handleFieldChange('multiSelect', e.target.checked)}
              />
              <span className="ml-2">Allow multiple selections</span>
            </Label>
          </div>

          <div>
            <Label htmlFor="decider-type">Who decides?</Label>
            <Select
              id="decider-type"
              value={value.deciderType || 'Users'}
              onChange={(e) => handleFieldChange('deciderType', e.target.value)}
            >
              <option value="Users">Specific User</option>
              <option value="Everyone">Anyone</option>
            </Select>
            {touched.deciderType && errors.deciderType && (
              <p className="mt-1 text-sm text-red-600">{errors.deciderType}</p>
            )}
          </div>

          {(value.deciderType === 'Users') && (
            <SelectWorkspaceUserId
              value={value.deciderId || ''}
              onChange={(selectedValue) => handleFieldChange('deciderId', selectedValue as string)}
              label="Select Decision Maker"
              placeholder="Search for a user to make branch decisions..."
              error={touched.deciderId ? errors.deciderId : undefined}
            />
          )}

          <div>
            <Label>Branch Options</Label>
            <p className="text-sm text-cm-text-muted mb-2">
              Add options for the decision maker to choose from
            </p>
            <div className="space-y-2">
              {Object.entries(value.branchOptions || {}).map(([id, label]) => (
                <div key={id} className="flex items-center gap-2">
                  <TextInput
                    value={label}
                    onChange={(e) => {
                      const newOptions = { ...value.branchOptions };
                      newOptions[id] = e.target.value;
                      handleFieldChange('branchOptions', newOptions);
                    }}
                    placeholder="Branch option label"
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newOptions = { ...value.branchOptions };
                      delete newOptions[id];
                      handleFieldChange('branchOptions', newOptions);
                    }}
                    className="px-2 py-1 text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const newId = crypto.randomUUID();
                  const newOptions = { ...value.branchOptions, [newId]: '' };
                  handleFieldChange('branchOptions', newOptions);
                }}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Add Branch Option
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowStepConditionForm;

// Helper function to convert form data to WorkflowStepCondition
export const formDataToWorkflowStepCondition = (formData: WorkflowStepConditionFormData): sched.WorkflowStepCondition => {
  switch (formData.type) {
    case 'Immediate':
      return { type: 'Immediate' };

    case 'Manual': {
      let by: sched.WorkflowAuthorizationSubject;
      if (formData.actorType === 'Users') {
        by = { type: 'Users', ids: [formData.actorId || ''] };
      } else if (formData.actorType === 'Parameter') {
        by = { type: 'Parameter', key: formData.actorId || '' };
      } else {
        by = { type: 'Everyone' };
      }
      const hasEstimate = formData.workEstimateMin != null || formData.workEstimateExpected != null || formData.workEstimateMax != null;
      const work_estimate: sched.BoundedWorkEstimate | undefined = hasEstimate
        ? { min: formData.workEstimateMin, expected: formData.workEstimateExpected, max: formData.workEstimateMax }
        : undefined;
      return { type: 'Manual', by, work_estimate };
    }

    case 'TimeElapsed':
      return { type: 'TimeElapsed', duration_seconds: formData.durationSeconds || 0 };

    case 'TimeReached':
      return { type: 'TimeReached', time: formData.targetTime || new Date().toISOString() };

    case 'WorkflowStateQuery':
      return { type: 'WorkflowStateQuery', state: formData.stateQuery || '' };

    case 'Branch':
      const decider: sched.WorkflowAuthorizationSubject = formData.deciderType === 'Everyone'
        ? { type: 'Everyone' }
        : {
          type: 'Users', ids: [formData.deciderId || '']
        }
      return {
        type: 'Branch',
        options: formData.branchOptions || {},
        multi_select: formData.multiSelect || false,
        decider
      };

    default:
      return { type: 'Immediate' };
  }
};

// Helper function to convert WorkflowStepCondition to form data
export const workflowStepConditionToFormData = (condition: sched.WorkflowStepCondition): WorkflowStepConditionFormData => {
  switch (condition.type) {
    case 'Immediate':
      return { type: 'Immediate' };

    case 'Manual': {
      const by = condition.by as any;
      const est = (condition as any).work_estimate as sched.BoundedWorkEstimate | undefined;
      let actorId: string | undefined;
      if (by.type === 'Users') actorId = by.ids?.[0];
      else if (by.type === 'Parameter') actorId = by.key;
      return {
        type: 'Manual',
        actorType: by.type,
        actorId,
        workEstimateMin: est?.min,
        workEstimateExpected: est?.expected,
        workEstimateMax: est?.max,
      };
    }

    case 'TimeElapsed':
      return {
        type: 'TimeElapsed',
        durationSeconds: condition.duration_seconds,
      };

    case 'TimeReached':
      return {
        type: 'TimeReached',
        targetTime: condition.time,
      };

    case 'WorkflowStateQuery':
      return {
        type: 'WorkflowStateQuery',
        stateQuery: condition.state,
      };

    case 'Branch':
      return {
        type: 'Branch',
        branchOptions: condition.options,
        multiSelect: condition.multi_select,
        deciderType: condition.decider.type as 'Users' | 'Everyone',
        // @ts-expect-error what a mess AI wrote for me
        deciderId: condition.decider?.ids?.[0],
      };

    default:
      return { type: 'Immediate' };
  }
};
